import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  Task,
  Image,
  Comment,
  CreateTaskInput,
  UpdateTaskInput,
  CreateCommentInput,
  ApiSuccessResponse,
  ApiErrorResponse,
  UploadProgress,
} from '../types';

// Get API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

/**
 * Axios instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Response interceptor for error handling
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    // Handle network errors
    if (!error.response) {
      throw new Error('Network error. Please check your connection.');
    }

    // Handle API errors
    const errorResponse = error.response.data;
    if (errorResponse && !errorResponse.success) {
      throw new Error(errorResponse.error.message || 'An error occurred');
    }

    throw error;
  }
);

/**
 * Task API Service
 */
export const taskApi = {
  /**
   * Get all tasks with optional filtering
   */
  async getTasks(completed?: boolean): Promise<Task[]> {
    const params = completed !== undefined ? { completed } : {};
    const response = await apiClient.get<ApiSuccessResponse<Task[]>>('/api/tasks', { params });
    
    // Add image URLs
    return response.data.data.map(task => ({
      ...task,
      images: task.images?.map(img => ({
        ...img,
        url: `${API_URL}/api/images/${img.filename}`,
      })),
    }));
  },

  /**
   * Get a single task by ID
   */
  async getTask(id: number): Promise<Task> {
    const response = await apiClient.get<ApiSuccessResponse<Task>>(`/api/tasks/${id}`);
    const task = response.data.data;
    
    // Add image URLs
    return {
      ...task,
      images: task.images?.map(img => ({
        ...img,
        url: `${API_URL}/api/images/${img.filename}`,
      })),
    };
  },

  /**
   * Create a new task
   */
  async createTask(input: CreateTaskInput): Promise<Task> {
    const response = await apiClient.post<ApiSuccessResponse<Task>>('/api/tasks', input);
    return response.data.data;
  },

  /**
   * Update an existing task
   */
  async updateTask(id: number, input: UpdateTaskInput): Promise<Task> {
    const response = await apiClient.put<ApiSuccessResponse<Task>>(`/api/tasks/${id}`, input);
    return response.data.data;
  },

  /**
   * Delete a task
   */
  async deleteTask(id: number): Promise<void> {
    await apiClient.delete(`/api/tasks/${id}`);
  },

  /**
   * Toggle task completion status
   */
  async toggleTask(id: number): Promise<Task> {
    const response = await apiClient.patch<ApiSuccessResponse<Task>>(`/api/tasks/${id}/toggle`);
    return response.data.data;
  },
};

/**
 * Image API Service
 */
export const imageApi = {
  /**
   * Upload images for a task
   */
  async uploadImages(
    taskId: number,
    files: File[],
    onProgress?: (progress: UploadProgress) => void
  ): Promise<Image[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    const response = await apiClient.post<ApiSuccessResponse<Image[]>>(
      `/api/tasks/${taskId}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage,
            });
          }
        },
      }
    );

    // Add image URLs
    return response.data.data.map(img => ({
      ...img,
      url: `${API_URL}/api/images/${img.filename}`,
    }));
  },

  /**
   * Delete an image
   */
  async deleteImage(id: number): Promise<void> {
    await apiClient.delete(`/api/images/${id}`);
  },

  /**
   * Get image URL
   */
  getImageUrl(filename: string): string {
    return `${API_URL}/api/images/${filename}`;
  },
};

/**
 * Health check
 */
export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/health');
    return response.status === 200;
  } catch {
    return false;
  }
};

/**
 * Comment API Service
 */
export const commentApi = {
  /**
   * Get all comments for a task
   */
  async getComments(taskId: number): Promise<Comment[]> {
    const response = await apiClient.get<ApiSuccessResponse<Comment[]>>(
      `/api/tasks/${taskId}/comments`
    );
    return response.data.data;
  },

  /**
   * Create a new comment
   */
  async createComment(taskId: number, input: CreateCommentInput): Promise<Comment> {
    const response = await apiClient.post<ApiSuccessResponse<Comment>>(
      `/api/tasks/${taskId}/comments`,
      input
    );
    return response.data.data;
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId: number): Promise<void> {
    await apiClient.delete(`/api/comments/${commentId}`);
  },
};

export default {
  taskApi,
  imageApi,
  commentApi,
  healthCheck,
};
